import {
  DetailedInsight,
  InsightsEpisode,
  InsightsPrediction,
} from '@api/api/insights';
import { TSDatum } from '@api/api/timeseries-schema';
import { Formats, formatDateToLocale } from '@helpers/utils/dates/dates';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { toZonedTime, format as tzFormat } from 'date-fns-tz';
import { TFunction } from 'i18next';

export const getInsightTotalTradingDays = (insight: DetailedInsight) => {
  return insight.episodes.reduce((acc, ep) => acc + ep.events.length, 0);
};

export const getInsightEpisodeDates = (episodes: InsightsEpisode[]) => {
  const languageSetting = useLanguageStore.getState().languageSetting;
  return episodes.map(episode =>
    formatDateToLocale(
      languageSetting,
      new Date(episode.start_date),
      Formats['yyyy-MM-dd']
    )
  );
};

export const filterPredictions = (
  predictions: InsightsPrediction[],
  indexMax: number
) => predictions.filter((_, i) => i <= indexMax);

export const getInsightCondition = (insight: DetailedInsight, t: TFunction) => {
  const conditionStrings = insight.conditions
    .map(condition => condition.condition_text.trim())
    .filter(Boolean);

  if (!conditionStrings.length) {
    return '';
  }

  const andLabel = t('scenarioCommander:domino.and');
  return conditionStrings.join(`${andLabel}\n`);
};

export const formatCumulativeReturnSnake = (
  data: TSDatum[],
  startDate: string
) => {
  const foundIndex = data.findIndex(
    d => d.index.slice(0, 10) === startDate.slice(0, 10)
  );
  const startIndex = foundIndex === -1 ? 0 : foundIndex;
  return data.slice(startIndex).map((d, idx) => ({
    index: idx,
    value: d.value,
  }));
};

export const formatUtcDate = (dateString: string, pattern: string) => {
  const languageSetting = useLanguageStore.getState().languageSetting;
  const isValidPattern = Object.hasOwn(Formats, pattern);
  const utcDate = toZonedTime(new Date(dateString), 'UTC');
  return isValidPattern
    ? formatDateToLocale(
        languageSetting,
        utcDate,
        Formats[pattern as keyof typeof Formats]
      )
    : tzFormat(utcDate, pattern);
};
